import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CommunityHub: React.FC = () => {
  // Page Settings State
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Price Calculator State
  const [selectedDay, setSelectedDay] = useState<'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'>('monday');
  const [startTime, setStartTime] = useState<number>(9); // Hour in 24h format
  const [duration, setDuration] = useState<number>(2);
  const [organizationType, setOrganizationType] = useState<'non-profit' | 'profit'>('non-profit');
  const [hireType, setHireType] = useState<'standard' | 'pch'>('standard');
  const [hasFood, setHasFood] = useState<boolean>(false);
  const [isLongTerm, setIsLongTerm] = useState<boolean>(false);
  const [storageNeeded, setStorageNeeded] = useState<'none' | 'shared' | 'sole'>('none');
  const [showOrgTooltip, setShowOrgTooltip] = useState<boolean>(false);
  const [showHireTooltip, setShowHireTooltip] = useState<boolean>(false);
  const [showDepositTooltip, setShowDepositTooltip] = useState<boolean>(false);

  // Booking form state
  const [bookingHireType, setBookingHireType] = useState<'private' | 'non-profit'>('non-profit');
  const [bookingFormData, setBookingFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    postCode: '',
    eventType: '',
    eventDate: '',
    startTime: '',
    endTime: '',
    numberOfGuests: '',
    foodAndDrinks: 'no',
    charityNumber: '',
    additionalRequirements: '',
    agreeToTerms: false
  });
  const [bookingMessage, setBookingMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch page settings
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/pages`);
      if (response.data.success) {
        setSettings(response.data.data.communityHubPage);
      }
    } catch (error) {
      console.error('Error fetching hub settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-set hire type based on organization type
  const handleOrganizationTypeChange = (type: 'non-profit' | 'profit') => {
    setOrganizationType(type);
    if (type === 'non-profit') {
      setHireType('standard');
      // Set minimum duration based on hire type
      if (duration < 1) setDuration(1);
    } else {
      setHireType('pch');
      // Set minimum duration for private hire
      if (duration < 4) setDuration(4);
    }
  };

  // Update duration validation when organization type changes
  const getMinDuration = () => {
    return organizationType === 'profit' ? 4 : 1;
  };

  // Handle booking form submission
  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setBookingMessage('');

    // Validate end time is not after 10pm
    if (bookingFormData.endTime && bookingFormData.endTime > '22:00') {
      setBookingMessage('Error: Bookings cannot end after 10:00 PM as the centre closes at this time.');
      setIsSubmitting(false);
      return;
    }

    // Validate minimum booking duration
    const minHours = bookingHireType === 'private' ? 4 : 1;
    if (bookingFormData.startTime && bookingFormData.endTime) {
      const start = new Date(`2000-01-01 ${bookingFormData.startTime}`);
      const end = new Date(`2000-01-01 ${bookingFormData.endTime}`);
      const diffHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
      
      if (diffHours < minHours) {
        setBookingMessage(`Error: ${bookingHireType === 'private' ? 'Private' : 'Non-profit'} bookings require a minimum of ${minHours} hour${minHours > 1 ? 's' : ''}.`);
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const response = await fetch('http://localhost:5000/api/hub/book', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...bookingFormData,
          hireType: bookingHireType
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setBookingMessage('Thank you for your enquiry! A member of our team will get back to you within 24 hours.');
        // Reset form
        setBookingFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: '',
          postCode: '',
          eventType: '',
          eventDate: '',
          startTime: '',
          endTime: '',
          numberOfGuests: '',
          foodAndDrinks: 'no',
          charityNumber: '',
          additionalRequirements: '',
          agreeToTerms: false
        });
      } else {
        setBookingMessage(data.message || 'Error submitting booking. Please try again.');
      }
    } catch (error) {
      setBookingMessage('Error submitting booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setBookingFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  // Charging Band Matrix
  const getChargingBand = (day: string, hour: number): 'nominal' | 'bronze' | 'silver' | 'gold' | 'pch' => {
    const isWeekend = day === 'saturday' || day === 'sunday';
    const isWeekday = !isWeekend;
    const isFridayAfternoon = day === 'friday' && hour >= 10 && hour <= 14;

    if (isWeekend) return 'gold';
    if (hour === 9) return 'nominal';
    if (isFridayAfternoon) return 'pch';
    if (hour >= 10 && hour <= 14) return 'bronze';
    if (hour >= 15 && hour <= 15) return 'bronze';
    if (hour >= 16 && hour <= 21) return 'silver';
    return 'bronze';
  };

  // Pricing Logic Based on Hirer Agreement
  const calculatePrice = () => {
    let totalBaseCost = 0;
    const hourBreakdown: Array<{hour: number, band: string, rate: number}> = [];

    // Calculate cost for each hour
    for (let i = 0; i < duration; i++) {
      const currentHour = startTime + i;
      if (currentHour >= 22) break; // Hub closes at 21:00

      let band = getChargingBand(selectedDay, currentHour);
      let hourlyRate = 0;

      // Private/Commercial Hire (PCH) has fixed rate
      if (hireType === 'pch') {
        hourlyRate = 50;
        band = 'pch';
      } else {
        // Standard rates based on band and organization type
        switch (band) {
          case 'nominal':
            hourlyRate = organizationType === 'non-profit' ? 0 : 30; // Custom rate, using £30 for profit
            break;
          case 'bronze':
            hourlyRate = organizationType === 'non-profit' ? 20 : 30;
            break;
          case 'silver':
            hourlyRate = organizationType === 'non-profit' ? 25 : 35;
            break;
          case 'gold':
            hourlyRate = organizationType === 'non-profit' ? 30 : 40;
            break;
          case 'pch':
            hourlyRate = organizationType === 'non-profit' ? 20 : 30; // Friday afternoon defaults to bronze rate
            break;
        }
      }

      totalBaseCost += hourlyRate;
      hourBreakdown.push({
        hour: currentHour,
        band: band.toUpperCase(),
        rate: hourlyRate
      });
    }

    // Cleaning Fee
    let cleaningFee = 0;
    if (isLongTerm) {
      cleaningFee = 20; // £20 per month for long term
    } else {
      if (duration > 2 || hasFood) {
        cleaningFee = 30; // £30 per hire
      } else {
        cleaningFee = 0; // Free for short hires under 2 hours without food
      }
    }

    // Storage Fees (monthly)
    let storageFee = 0;
    if (storageNeeded === 'sole') {
      storageFee = 20; // £20 per month
    } else if (storageNeeded === 'shared') {
      storageFee = 10; // Average of £5-15 range
    }

    // Deposit (for short-term bookings)
    const deposit = isLongTerm ? 0 : 200;

    const totalCost = totalBaseCost + cleaningFee + storageFee + deposit;

    return {
      baseCost: totalBaseCost,
      cleaningFee,
      storageFee,
      deposit,
      totalCost,
      hourBreakdown
    };
  };

  const pricing = calculatePrice();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  // Scroll to section function
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section - Matching Homepage Theme */}
      <section 
        className="bg-gradient-to-r from-primary to-primary-dark text-white py-20 bg-cover bg-center"
        style={{
          backgroundImage: settings?.hero?.backgroundImage 
            ? `linear-gradient(rgba(37, 99, 235, 0.8), rgba(30, 64, 175, 0.8)), url(http://localhost:5000${settings.hero.backgroundImage})`
            : undefined
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Community Hub
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-100">
              {settings?.hero?.subtitle || 'A modern venue for your events and gatherings'}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button
                onClick={() => scrollToSection('booking')}
                className="btn-secondary text-lg px-8 py-4"
              >
                {settings?.hero?.buttonText || 'Book Now'}
              </button>
              <button
                onClick={() => scrollToSection('calculator')}
                className="bg-white text-primary hover:bg-gray-100 font-semibold py-4 px-8 rounded-lg transition duration-300 text-lg"
              >
                {settings?.hero?.secondaryButtonText || 'View Pricing'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Description Section */}
      {settings?.description && (
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-lg text-gray-700 text-center leading-relaxed">
              {settings.description}
            </p>
          </div>
        </section>
      )}

      {/* Features Section */}
      {settings?.features && settings.features.length > 0 && (
        <section className="py-12 bg-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Our Features</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {settings.features.map((feature: any, index: number) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-8 text-center">
                  <div className="text-5xl mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Hub Image Gallery Section */}
      {settings?.images && settings.images.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">Hub Gallery</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {settings.images.map((img: string, idx: number) => (
                <div key={idx} className="overflow-hidden rounded-lg shadow-md hover:shadow-xl transition">
                  <img
                    src={`http://localhost:5000${img}`}
                    alt={`Hub ${idx + 1}`}
                    className="w-full h-64 object-cover hover:scale-110 transition duration-300"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Google Maps - Leftley Sports Club Community Hub */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-4">Location</h2>
          <p className="text-gray-600 mb-4 text-lg">
            <strong>📍 Address:</strong> Leftley Sports Club Community Hub, Martin Peters Court, Meadow Rd, Barking IG11 9FE
          </p>
          <div className="bg-white rounded-lg shadow-md overflow-hidden relative">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2481.957446793982!2d0.1010351!3d51.5365525!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a55988e4ec03%3A0x43db869fcd8c4d9d!2sLeftley%20Sports%20Club%2C%20Community%20Hub!5e0!3m2!1sen!2suk!4v1733930000000!5m2!1sen!2suk"
              width="100%"
              height="300"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
          <div className="mt-4 p-4 bg-blue-50 border-l-4 border-primary rounded">
            <p className="text-gray-700">
              <strong>🕒 Opening Hours:</strong> 9:00 AM - 10:00 PM
            </p>
            <p className="text-gray-700 mt-2">
              <strong>🚗 Parking:</strong> Free parking on weekends. Mon-Fri: CPZ restrictions apply (1:30 PM - 2:30 PM)
            </p>
            <p className="text-gray-700 mt-2">
              <strong>🚌 Transport:</strong> 2-minute walk from Upney Station
            </p>
          </div>
        </div>

        {/* Hub Agreement */}
        <div className="mb-12 bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold mb-4">Hire Agreement</h2>
          <p className="text-gray-600 mb-4">
            Please review our community hub hire agreement before booking.
          </p>
          <a
            href="https://www.leftleysportsclub.org.uk/wp-content/uploads/2024/05/Community-Hub-Hire-Agreement-1.0.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-block"
          >
            Download Hire Agreement (PDF)
          </a>
        </div>

        {/* Price Calculator */}
        <div id="calculator" className="mb-12 bg-white rounded-lg shadow-md p-8 scroll-mt-20">
          <h2 className="text-2xl font-bold mb-6">Price Calculator</h2>
          <p className="text-gray-600 mb-6">
            Use this calculator to estimate your hall hire cost. All prices are based on our official hire agreement.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Day Selection */}
            <div>
              <label className="label">Day of Week *</label>
              <select 
                className="input-field"
                value={selectedDay}
                onChange={(e) => setSelectedDay(e.target.value as any)}
              >
                <option value="monday">Monday</option>
                <option value="tuesday">Tuesday</option>
                <option value="wednesday">Wednesday</option>
                <option value="thursday">Thursday</option>
                <option value="friday">Friday</option>
                <option value="saturday">Saturday</option>
                <option value="sunday">Sunday</option>
              </select>
            </div>

            {/* Start Time */}
            <div>
              <label className="label">Start Time *</label>
              <select 
                className="input-field"
                value={startTime}
                onChange={(e) => setStartTime(Number(e.target.value))}
              >
                <option value={9}>09:00 (9am)</option>
                <option value={10}>10:00 (10am)</option>
                <option value={11}>11:00 (11am)</option>
                <option value={12}>12:00 (12pm)</option>
                <option value={13}>13:00 (1pm)</option>
                <option value={14}>14:00 (2pm)</option>
                <option value={15}>15:00 (3pm)</option>
                <option value={16}>16:00 (4pm)</option>
                <option value={17}>17:00 (5pm)</option>
                <option value={18}>18:00 (6pm)</option>
                <option value={19}>19:00 (7pm)</option>
                <option value={20}>20:00 (8pm)</option>
                <option value={21}>21:00 (9pm)</option>
              </select>
            </div>

            {/* Duration */}
            <div>
              <label className="label">Duration (Hours) *</label>
              <input 
                type="number" 
                min={getMinDuration()} 
                max="12"
                className="input-field"
                value={duration}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (val >= getMinDuration()) {
                    setDuration(val);
                  }
                }}
              />
              <p className="text-xs text-red-600 mt-1 font-semibold">
                {organizationType === 'profit' 
                  ? 'Minimum 4 hours for Private Hire' 
                  : 'Minimum 1 hour for Non-Profit Hire'}
              </p>
              <p className="text-xs text-gray-500 mt-1">Hub closes at 22:00 (10pm)</p>
            </div>

            {/* Organization Type */}
            <div>
              <label className="label flex items-center gap-2">
                Organization Type *
                <div className="relative inline-block">
                  <span 
                    className="inline-flex items-center justify-center w-4 h-4 text-xs bg-blue-500 text-white rounded-full cursor-help"
                    onMouseEnter={() => setShowOrgTooltip(true)}
                    onMouseLeave={() => setShowOrgTooltip(false)}
                  >
                    i
                  </span>
                  {showOrgTooltip && (
                    <div className="absolute z-10 w-64 p-3 text-sm bg-gray-800 text-white rounded shadow-lg -top-2 left-6">
                      <p className="font-semibold mb-1">Non-Profit/Charity:</p>
                      <p className="mb-2">Community groups, charities, non-profit organizations, religious groups, support groups</p>
                      <p className="font-semibold mb-1">For-Profit/Commercial:</p>
                      <p>Mehendi parties, birthday parties, wedding receptions, baby showers, commercial events, fitness classes, private tutoring</p>
                    </div>
                  )}
                </div>
              </label>
              <select 
                className="input-field"
                value={organizationType}
                onChange={(e) => handleOrganizationTypeChange(e.target.value as 'non-profit' | 'profit')}
              >
                <option value="non-profit">Non-Profit / Charity</option>
                <option value="profit">For-Profit / Commercial</option>
              </select>
            </div>

            {/* Hire Type */}
            <div>
              <label className="label flex items-center gap-2">
                Hire Type *
                <div className="relative inline-block">
                  <span 
                    className="inline-flex items-center justify-center w-4 h-4 text-xs bg-blue-500 text-white rounded-full cursor-help"
                    onMouseEnter={() => setShowHireTooltip(true)}
                    onMouseLeave={() => setShowHireTooltip(false)}
                  >
                    i
                  </span>
                  {showHireTooltip && (
                    <div className="absolute z-10 w-64 p-3 text-sm bg-gray-800 text-white rounded shadow-lg -top-2 left-6">
                      <p className="font-semibold mb-1">Standard Hire:</p>
                      <p className="mb-2">Regular community hall hire with time-based charging bands (Nominal, Bronze, Silver, Gold)</p>
                      <p className="font-semibold mb-1">Private/Commercial Hire (PCH):</p>
                      <p>Flat rate of £50/hour regardless of time or day. Ideal for commercial events, private parties, or for-profit activities</p>
                    </div>
                  )}
                </div>
              </label>
              <select 
                className="input-field"
                value={hireType}
                onChange={(e) => setHireType(e.target.value as 'standard' | 'pch')}
                disabled={true}
                style={{ 
                  backgroundColor: '#f3f4f6', 
                  cursor: 'not-allowed',
                  opacity: 0.6 
                }}
              >
                <option value="standard">
                  {organizationType === 'non-profit' ? 'Standard Hire (Auto-selected)' : 'Standard Hire'}
                </option>
                <option value="pch">
                  {organizationType === 'profit' ? 'Private/Commercial Hire - £50/hr (Auto-selected)' : 'Private/Commercial Hire - £50/hr'}
                </option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {organizationType === 'non-profit' 
                  ? 'Non-profit organizations use Standard Hire rates' 
                  : 'For-profit bookings use Private/Commercial Hire rates'}
              </p>
            </div>

            {/* Food/Drinks */}
            <div>
              <label className="label">Food/Drinks Being Served? *</label>
              <select 
                className="input-field"
                value={hasFood ? 'yes' : 'no'}
                onChange={(e) => setHasFood(e.target.value === 'yes')}
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Affects cleaning fee</p>
            </div>

            {/* Long Term Hire */}
            <div>
              <label className="label">Is This a Long-Term Hire? *</label>
              <select 
                className="input-field"
                value={isLongTerm ? 'yes' : 'no'}
                onChange={(e) => setIsLongTerm(e.target.value === 'yes')}
              >
                <option value="no">No (One-time or Short-term)</option>
                <option value="yes">Yes (Regular/Monthly)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Long-term hires get reduced cleaning fees</p>
            </div>

            {/* Storage */}
            <div>
              <label className="label">Storage Required *</label>
              <select 
                className="input-field"
                value={storageNeeded}
                onChange={(e) => setStorageNeeded(e.target.value as 'none' | 'shared' | 'sole')}
              >
                <option value="none">No Storage Needed</option>
                <option value="shared">Shared Storage (£10/month)</option>
                <option value="sole">Sole Use Storage (£20/month)</option>
              </select>
            </div>
          </div>

          {/* Price Breakdown */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded">
            <h3 className="text-xl font-bold mb-4 text-blue-900">Estimated Cost Breakdown</h3>
            
            {/* Hourly Breakdown */}
            <div className="mb-4">
              <h4 className="font-semibold text-gray-800 mb-2">Hourly Rate Breakdown:</h4>
              <div className="bg-white rounded p-3 space-y-1 text-sm">
                {pricing.hourBreakdown.map((item, index) => (
                  <div key={index} className="flex justify-between">
                    <span className="text-gray-600">
                      {item.hour}:00 - {item.hour + 1}:00 
                      <span className="ml-2 text-xs bg-gray-200 px-2 py-0.5 rounded">
                        {item.band}
                      </span>
                    </span>
                    <span className="font-semibold">£{item.rate.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2 text-gray-700">
              <div className="flex justify-between font-semibold">
                <span>Base Hire Cost ({duration} hours):</span>
                <span>£{pricing.baseCost.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between">
                <span>
                  Cleaning Fee 
                  {isLongTerm ? ' (Long-term - Monthly)' : 
                   (duration <= 2 && !hasFood) ? ' (Short hire, no food - Free)' : 
                   ' (Over 2hrs or with food)'}:
                </span>
                <span className="font-semibold">£{pricing.cleaningFee.toFixed(2)}</span>
              </div>
              
              {storageNeeded !== 'none' && (
                <div className="flex justify-between">
                  <span>
                    {storageNeeded === 'sole' ? 'Sole Use Storage' : 'Shared Storage'} (Monthly):
                  </span>
                  <span className="font-semibold">£{pricing.storageFee.toFixed(2)}</span>
                </div>
              )}

              {!isLongTerm && (
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-2">
                    Refundable Deposit:
                    <div className="relative inline-block">
                      <span 
                        className="inline-flex items-center justify-center w-4 h-4 text-xs bg-blue-500 text-white rounded-full cursor-help"
                        onMouseEnter={() => setShowDepositTooltip(true)}
                        onMouseLeave={() => setShowDepositTooltip(false)}
                      >
                        i
                      </span>
                      {showDepositTooltip && (
                        <div className="absolute z-10 w-72 p-3 text-sm bg-gray-800 text-white rounded shadow-lg bottom-6 left-0">
                          <p className="font-semibold mb-2">£200 Refundable Deposit</p>
                          <p>All short-term bookings require a £200 deposit. This deposit is fully refundable provided:</p>
                          <ul className="list-disc ml-4 mt-2 space-y-1">
                            <li>No damage to the hall</li>
                            <li>No properties missing</li>
                            <li>Guests leave on time</li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </span>
                  <span className="font-semibold text-green-600">£{pricing.deposit.toFixed(2)}</span>
                </div>
              )}

              <div className="border-t-2 border-gray-300 pt-2 mt-2">
                <div className="flex justify-between text-lg font-bold text-blue-900">
                  <span>TOTAL ESTIMATED COST:</span>
                  <span>£{pricing.totalCost.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Pricing Legend */}
            <div className="mt-4 p-3 bg-white rounded text-xs">
              <p className="font-semibold mb-2">Charging Band Rates ({organizationType === 'non-profit' ? 'Non-Profit/Charity' : 'For-Profit'}):</p>
              <div className="grid grid-cols-2 gap-2">
                <div><span className="bg-gray-200 px-2 py-0.5 rounded">NOMINAL</span> £{organizationType === 'non-profit' ? '0' : '30'}/hr</div>
                <div><span className="bg-gray-200 px-2 py-0.5 rounded">BRONZE</span> £{organizationType === 'non-profit' ? '20' : '30'}/hr</div>
                <div><span className="bg-gray-200 px-2 py-0.5 rounded">SILVER</span> £{organizationType === 'non-profit' ? '25' : '35'}/hr</div>
                <div><span className="bg-gray-200 px-2 py-0.5 rounded">GOLD</span> £{organizationType === 'non-profit' ? '30' : '40'}/hr</div>
              </div>
              {hireType === 'pch' && (
                <p className="mt-2 text-orange-600 font-semibold">PCH Rate: £50/hour (flat rate)</p>
              )}
            </div>

            <div className="mt-4 text-sm text-gray-600">
              <p>* This is an estimate. Final pricing will be confirmed upon booking approval.</p>
              <p>* Deposit and payment terms as per hire agreement.</p>
              <p>* Additional charges may apply for extra services or damages.</p>
            </div>
          </div>
        </div>

        {/* Google Calendar */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-4">Availability Calendar</h2>
          <div className="bg-white rounded-lg shadow-md p-4">
            <iframe
              src="https://calendar.google.com/calendar/embed?src=21e35e56caeaf2fcb22c591a5ff2955ebc41b9bbf64e7d976c3e80633419de5c%40group.calendar.google.com&ctz=Europe%2FLondon"
              style={{ border: 0 }}
              width="100%"
              height="600"
              frameBorder="0"
              scrolling="no"
            ></iframe>
          </div>
        </div>

        {/* Booking Form */}
        <div id="booking" className="bg-white rounded-lg shadow-md p-8 scroll-mt-20">
          <h2 className="text-2xl font-bold mb-6">Book the Hub</h2>
          <p className="text-gray-600 mb-6">
            Complete the form below to request a booking. We'll get back to you shortly.
          </p>
          <p className="text-sm text-red-600 mb-4">* Indicates mandatory fields</p>

          {bookingMessage && (
            <div className={`mb-6 p-4 rounded ${bookingMessage.includes('Error') ? 'bg-red-50 border-l-4 border-red-500 text-red-700' : 'bg-green-50 border-l-4 border-green-500 text-green-700'}`}>
              <p className="font-semibold">{bookingMessage}</p>
            </div>
          )}
          
          <form onSubmit={handleBookingSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Name */}
            <div>
              <label className="label">First Name *</label>
              <input 
                type="text" 
                name="firstName"
                className="input-field" 
                value={bookingFormData.firstName}
                onChange={handleInputChange}
                required 
              />
            </div>

            {/* Last Name */}
            <div>
              <label className="label">Last Name *</label>
              <input 
                type="text" 
                name="lastName"
                className="input-field" 
                value={bookingFormData.lastName}
                onChange={handleInputChange}
                required 
              />
            </div>

            {/* Email */}
            <div>
              <label className="label">Email *</label>
              <input 
                type="email" 
                name="email"
                className="input-field" 
                value={bookingFormData.email}
                onChange={handleInputChange}
                required 
              />
            </div>

            {/* Phone Number */}
            <div>
              <label className="label">Phone Number *</label>
              <input 
                type="tel" 
                name="phone"
                className="input-field" 
                value={bookingFormData.phone}
                onChange={handleInputChange}
                required 
              />
            </div>

            {/* Home Address */}
            <div>
              <label className="label">Home Address *</label>
              <input 
                type="text" 
                name="address"
                className="input-field" 
                value={bookingFormData.address}
                onChange={handleInputChange}
                required 
              />
            </div>

            {/* Post Code */}
            <div>
              <label className="label">Post Code *</label>
              <input 
                type="text" 
                name="postCode"
                className="input-field" 
                value={bookingFormData.postCode}
                onChange={handleInputChange}
                required 
              />
            </div>

            {/* Event Type */}
            <div>
              <label className="label">Event Type *</label>
              <input 
                type="text" 
                name="eventType"
                className="input-field" 
                placeholder="e.g., Birthday Party, Meeting, Workshop"
                value={bookingFormData.eventType}
                onChange={handleInputChange}
                required 
              />
            </div>

            {/* Event Date */}
            <div>
              <label className="label">Event Date *</label>
              <input 
                type="date" 
                name="eventDate"
                className="input-field" 
                value={bookingFormData.eventDate}
                onChange={handleInputChange}
                required 
              />
            </div>

            {/* Start Time */}
            <div>
              <label className="label">Start Time *</label>
              <input 
                type="time" 
                name="startTime"
                className="input-field" 
                value={bookingFormData.startTime}
                onChange={handleInputChange}
                required 
              />
            </div>

            {/* End Time */}
            <div>
              <label className="label">End Time *</label>
              <input 
                type="time" 
                name="endTime"
                className="input-field" 
                max="22:00"
                value={bookingFormData.endTime}
                onChange={handleInputChange}
                required 
              />
              <p className="text-xs text-red-600 mt-1">Centre closes at 10:00 PM - bookings must end by this time</p>
            </div>

            {/* Number of Guests */}
            <div className="md:col-span-2">
              <label className="label">Number of Guests Expected *</label>
              <input 
                type="number" 
                name="numberOfGuests"
                min="1"
                className="input-field" 
                value={bookingFormData.numberOfGuests}
                onChange={handleInputChange}
                required 
              />
            </div>

            {/* Food and Drinks */}
            <div className="md:col-span-2">
              <label className="label">Will there be Food and Drinks? *</label>
              <select 
                name="foodAndDrinks"
                className="input-field"
                value={bookingFormData.foodAndDrinks}
                onChange={handleInputChange}
                required
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">This affects cleaning fee calculations</p>
            </div>

            {/* Hire Type Selection */}
            <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded p-4">
              <label className="flex items-center mb-2">
                <input 
                  type="checkbox" 
                  className="mr-3 w-4 h-4"
                  checked={bookingHireType === 'private'}
                  onChange={(e) => setBookingHireType(e.target.checked ? 'private' : 'non-profit')}
                />
                <span className="font-semibold text-gray-800">
                  I am a Private Hirer (For-profit events, parties, commercial use)
                </span>
              </label>
              <label className="flex items-center">
                <input 
                  type="checkbox" 
                  className="mr-3 w-4 h-4"
                  checked={bookingHireType === 'non-profit'}
                  onChange={(e) => setBookingHireType(e.target.checked ? 'non-profit' : 'private')}
                />
                <span className="font-semibold text-gray-800">
                  I am a Non-Profit Hirer (Charity, community group, non-profit organization)
                </span>
              </label>
              <p className="text-sm text-red-600 mt-3 font-semibold">
                {bookingHireType === 'private' 
                  ? '⚠ Private Hire requires a minimum booking of 4 hours' 
                  : '✓ Non-Profit Hire requires a minimum booking of 1 hour'}
              </p>
            </div>

            {/* Charity Number - Only show for non-profit */}
            {bookingHireType === 'non-profit' && (
              <div className="md:col-span-2">
                <label className="label">Charity Number *</label>
                <input 
                  type="text" 
                  name="charityNumber"
                  className="input-field" 
                  placeholder="Enter your charity registration number"
                  value={bookingFormData.charityNumber}
                  onChange={handleInputChange}
                  required 
                />
                <p className="text-xs text-gray-500 mt-1">Required for non-profit bookings</p>
              </div>
            )}

            {/* Additional Requirements */}
            <div className="md:col-span-2">
              <label className="label">Additional Requirements</label>
              <textarea 
                name="additionalRequirements"
                className="input-field" 
                rows={4}
                placeholder="Please specify any special requirements, equipment needed, or additional information"
                value={bookingFormData.additionalRequirements}
                onChange={handleInputChange}
              ></textarea>
            </div>

            {/* Hire Agreement Section */}
            <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded p-4">
              <h3 className="font-semibold text-gray-800 mb-2">Hire Agreement</h3>
              <p className="text-sm text-gray-600 mb-3">
                Please review our community hub hire agreement before booking.
              </p>
              <a
                href="https://www.leftleysportsclub.org.uk/wp-content/uploads/2024/05/Community-Hub-Hire-Agreement-1.0.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition text-sm"
              >
                Download Hire Agreement (PDF)
              </a>
            </div>

            {/* Agreement Checkbox */}
            <div className="md:col-span-2">
              <label className="flex items-center">
                <input 
                  type="checkbox" 
                  name="agreeToTerms"
                  className="mr-2 w-4 h-4" 
                  checked={bookingFormData.agreeToTerms}
                  onChange={handleInputChange}
                  required 
                />
                <span className="text-sm">
                  I agree to the Community Hub Hire Agreement and understand the terms and conditions *
                </span>
              </label>
            </div>

            {/* Submit Button */}
            <div className="md:col-span-2">
              <button 
                type="submit" 
                className="btn-primary w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Booking Request'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CommunityHub;
